import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, EffectFade, Thumbs, Virtual, Keyboard, Mousewheel,HashNavigation,History } from "swiper/modules";
import { Swiper as SwiperClass } from 'swiper/types';
import {TourCatalog, TourOperator, TripItinerary, OtherContent, Hotel} from './contentfulModel';

type SlideDescriptor =
    | { type: 'cover'; name: string; index: number }
    | { type: 'index'; name: string; index: number }
    | { type: 'package'; name: string; index: number; tourPackage: TripItinerary }
    | { type: 'other'; name: string; index: number; otherContent: OtherContent }
    | { type: 'hotels'; name: string; index: number; hotels: Hotel[] };
import { TourCatalogCover } from "./tourCatalogCover.tsx";
import { TourCatalogIndex } from "./tourCatalogIndex.tsx";
import { TourCatalogContent } from "./tourCatalogContent.tsx";

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/effect-fade';
import 'swiper/css/virtual';

// Import icons
import ArrowLeftIcon from "../assets/icons/arrowLeftIcon.svg?react";
import ArrowRightIcon from "../assets/icons/arrowRightIcon.svg?react";
import PagesThumbIcon from "../assets/icons/pagesThumbIcon.svg?react";
import FullScreenIcon from "../assets/icons/fullScreenIcon.svg?react";
import {TourCatalogAdditionalContent} from "./tourCatalogAdditionalContent.tsx";
import html2canvas from "html2canvas";
import { Helmet } from "react-helmet";
import {TourCatalogAdditionalContentHotels} from "./tourCatalogAdditionalContentHotels.tsx";
import classNames from "classnames";
import {cdnImageWidth, compareMiceSubcategories, isMiceCatalog} from "./utils.tsx";
import {MiceCatalogIndex, MiceIndexSelection} from "./miceCatalogIndex.tsx";
import {MiceCatalogContent, MicePackage} from "./miceCatalogContent.tsx";

const cdnUrl = "https://cdn.51trips.com/cdn-cgi/image/f=webp/catalogos/"

const ENV = import.meta.env.VITE_ENV_NAME || 'dev';

const storagePath = ENV === "dev" ? 'assets/tourCatalogs/' : 'tourCatalogs/';

export const ItineraryCatalogView = () => {
    const [swiperRef, setSwiperRef] = useState<SwiperClass>();
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [showThumb, setShowThumb] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [tourOperator, setTourOperator] = useState<TourOperator | null>(null);
    const [slidesContent, setSlidesContent] = useState<SlideDescriptor[]>([]);
    const [showControls, setShowControls] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [sliderHeight, setSliderHeight] = useState('');
    // canvasThumbSlides is only populated when ?createThumb=true (html2canvas path)
    const [canvasThumbSlides, setCanvasThumbSlides] = useState<React.ReactElement[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [slidesRef, setSlidesRef] = useState<React.RefObject<HTMLDivElement | null>[]>([]);
    const [thumbWidth, setThumbWidth] = useState(0);
    const [mode, setMode] = useState('normal');
    const [catName, setCatalogname] = useState<string>();
    // Ciudad/tipo del índice MICE: vive aquí porque el carrusel virtual desmonta el índice al alejarse
    const [miceSelection, setMiceSelection] = useState<MiceIndexSelection>({});
    const [tourCatalog, setTourCatalog] = useState<TourCatalog | null>(null);




    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const thumbColumnRef = useRef<HTMLDivElement>(null);
    const progressRef = useRef(null);
    const hasNavigatedRef = useRef(false);

    // Stable flag: URL won't change during the component lifetime
    const isCreatingThumbs = new URLSearchParams(window.location.search).get("createThumb") === "true";
    // Calculate and update slider height considering mobile navigation bars
    const updateSliderHeight = useCallback(() => {
        const isMobileView = window.innerWidth < 768;
        setIsMobile(isMobileView);

        // Use setTimeout to ensure proper calculation after orientation changes
        setTimeout(() => {
            const headerHeight = headerRef.current?.offsetHeight || 40;
            const windowHeight = window.innerHeight;

            // Account for iOS safari toolbar which can be ~75px at bottom
            const safeAreaBottom = isMobileView ? 15 : 0; // Extra safety margin for mobile

            // For iOS devices, use visual viewport height to handle dynamic toolbar
            const visibleHeight = window.visualViewport ?
                    window.visualViewport.height : windowHeight;

            const calculatedHeight = visibleHeight - headerHeight - safeAreaBottom;

            setSliderHeight(`${calculatedHeight}px`);

            // Force scroll to top
            window.scrollTo(0, 0);

            // Prevent body scrolling
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            document.body.style.position = isMobileView ? 'fixed' : '';
            document.body.style.width = isMobileView ? '100%' : '';
            document.body.style.height = isMobileView ? '100%' : '';

            // Auto-hide thumbnails on mobile
            if (isMobileView && showThumb) {
                setShowThumb(false);
            }
        }, 100);
    }, [showThumb]);

    useEffect(() => {
        updateThumbWidth();
        window.addEventListener("resize", updateThumbWidth);
        return () => window.removeEventListener("resize", updateThumbWidth);
    }, []);

    // Event listener for visual viewport resizing (handles keyboard appearance on mobile)
    useEffect(() => {
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', updateSliderHeight);
            return () => {
                window.visualViewport.removeEventListener('resize', updateSliderHeight);
            };
        }
    }, [updateSliderHeight]);


    // Detect screen size changes
    useEffect(() => {
        updateSliderHeight();

        const handleResize = () => {
            updateSliderHeight();
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', () => {
            // Delay calculation after orientation change to get accurate dimensions
            setTimeout(updateSliderHeight, 300);
        });

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', updateSliderHeight);
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
        };
    }, [showThumb, updateSliderHeight]);


    const updateThumbWidth = useCallback(() => {
        const windowWidth = window.innerWidth;
        const maxSliderWidth = 1440;
        const margin = (windowWidth - maxSliderWidth) / 2; // Espacio sobrante

        if (windowWidth > maxSliderWidth) {
            setThumbWidth(margin > 0 ? Math.max(margin, 140) : 0); // Limitar a 140px
        } else {
            setThumbWidth(200); // Si la ventana es pequeña, no mostramos thumbnails
        }
    }, []);


    useEffect(() => {
        let timer: number | null = null;

        const handleMouseMove = () => {
            setShowControls(true);

            if (timer !== null) {
                window.clearTimeout(timer);
            }

            timer = window.setTimeout(() => {
                setShowControls(false);
            }, isMobile ? 1000 : 2000);
        };

        const handleTouchStart = () => {
            setShowControls(true);

            if (timer !== null) {
                window.clearTimeout(timer);
            }

            timer = window.setTimeout(() => {
                setShowControls(false);
            }, isMobile ? 1000 : 2000);
        };


        window.addEventListener('mousemove', handleMouseMove, {
            passive: true
        });
        window.addEventListener('touchstart', handleTouchStart,{
            passive: true
        });



        timer = window.setTimeout(() => {
            setShowControls(false);
        }, 2000);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('touchstart', handleTouchStart);
            if (timer !== null) {
                window.clearTimeout(timer);
            }
        };
    }, []);

    useEffect(() => {

        //Get query params
        const urlParams = new URLSearchParams(window.location.search);
        const mode = urlParams.get('mode');
        if(mode) {
            setMode(mode)
        }

        async function fetchTourOperator() {
            //From url get paths
            const paths = window.location.pathname.split("/");

            let operatorName
            let catalogName
            if(window.location.host.includes("catalogos.travel")){
                operatorName = window.location.host.split('.')[0]
                catalogName = paths[1];
            } else {
                operatorName = paths[1];
                catalogName = paths[2];
            }
            //first letter to uppercase
            operatorName = operatorName.charAt(0).toUpperCase() + operatorName.slice(1);

            if (!operatorName || !catalogName) {
                setError("No tour operator specified");
                return;
            }
            try {
                const timestamp = new Date().getTime();
                const catalogUrl = `https://storage.googleapis.com/tripsapp-${ENV}-cdn/${storagePath}${operatorName.toLowerCase()}/catalogs.json?t=${timestamp}`;
                const configUrl = `https://storage.googleapis.com/tripsapp-${ENV}-cdn/${storagePath}${operatorName.toLowerCase()}/config.json?t=${timestamp}`;

                // Use fetch with cache: 'no-store' to bypass the cache
                const [catalogResponse, configResponse] = await Promise.all([fetch(catalogUrl), fetch(configUrl)]);
                if (!catalogResponse.ok || !configResponse.ok) {
                    throw new Error(`HTTP error! status: ${catalogResponse.status} ${configResponse.status}`);
                }
                const tourCatalogs: TourCatalog[] = await catalogResponse.json();
                const tourOperatorConfig: TourOperator = await configResponse.json();
                setTourOperator(tourOperatorConfig);
                setCatalogname(catalogName.toLowerCase())
                setTourCatalog(tourCatalogs?.filter(
                    (catalog) => catalog.name.toLowerCase().replace(" ", "%20") === catalogName.toLowerCase()
                )[0] || null);

            } catch (error) {
                console.error("Error fetching TourOperator data:", error);
            }
        }

        fetchTourOperator();
    }, []);

    const processFonts = (tourOperator: TourOperator) => {
        // URL de la fuente proporcionada por el usuario, o una por defecto
        const headingFontUrl = tourOperator.headingFontUrl || '';
        const textFontUrl = tourOperator.textFontUrl || '';

        // Nombres por defecto en caso de que no existan fuentes
        const defaultHeadingFont = 'Arial, sans-serif';
        const defaultTextFont = 'Arial, sans-serif';

        // Extraer el nombre de la fuente desde la URL
        const getFontFamilyFromUrl = (fontUrl) => {
            const match = fontUrl.match(/family=([^:&]+)/);
            return match ? match[1].replace(/\+/g, ' ') : null;
        };

        const headingFontFamily = getFontFamilyFromUrl(headingFontUrl) || defaultHeadingFont;
        const textFontFamily = getFontFamilyFromUrl(textFontUrl) || defaultTextFont;

        // Cargar las fuentes dinámicamente si las URLs están presentes
        const loadFont = (url: string) => {
            if (!url) return;
            const existingLink = document.querySelector(`link[href="${url}"]`);
            if (existingLink) return; // evitar duplicados

            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = url;
            document.head.appendChild(link);
        };

        loadFont(headingFontUrl);
        loadFont(textFontUrl);

        // Crear variables CSS con los nombres de las fuentes
        const style = document.createElement('style');
        style.innerHTML = `
    :root {
      --heading-font: '${headingFontFamily}', ${defaultHeadingFont};
      --text-font: '${textFontFamily}', ${defaultTextFont};
    }
  `;
        document.head.appendChild(style);
    };

    const goToSlide = useCallback((target: string | number) => {
        if (!swiperRef) return;

        let targetIndex: number;
        if (typeof target === 'string') {
            const targetNormalized = target.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
            const slideItem = slidesContent.find((slide) => slide.name.toLowerCase() === targetNormalized);
            if (!slideItem) return;
            targetIndex = slideItem.index - 1;
        } else {
            targetIndex = Number(target);
        }

        if (targetIndex < 0 || targetIndex > slidesContent.length - 1) return;

        const currentIndex = swiperRef.activeIndex;
        const distance = Math.abs(targetIndex - currentIndex);
        const virtualParams = swiperRef.params.virtual as { addSlidesBefore: number; addSlidesAfter: number };

        // Target is already within the rendered virtual window — navigate directly
        if (distance <= (virtualParams.addSlidesAfter ?? 2)) {
            swiperRef.slideTo(targetIndex, 0);
            return;
        }

        // Target is outside the virtual window: expand it to include the target so React
        // mounts the slide before Swiper moves there, preventing a blank frame.
        const forward = targetIndex > currentIndex;
        if (forward) {
            virtualParams.addSlidesAfter = distance + 1;
        } else {
            virtualParams.addSlidesBefore = distance + 1;
        }
        swiperRef.virtual.update(true); // triggers React re-render with the expanded range

        // Wait two frames for React to commit the new slides, then navigate
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                swiperRef.slideTo(targetIndex, 0);

                // Restore compact virtual window after the slide has settled
                requestAnimationFrame(() => {
                    virtualParams.addSlidesBefore = isCreatingThumbs ? slidesContent.length : 2;
                    virtualParams.addSlidesAfter = isCreatingThumbs ? slidesContent.length : 2;
                    swiperRef.virtual.update(true);
                });
            });
        });
    }, [swiperRef, slidesContent, isCreatingThumbs]);

    useEffect(() => {
        if (!tourOperator || !tourCatalog) return;

        processFonts(tourOperator);

        const descriptors: SlideDescriptor[] = [
            { type: 'cover', name: 'cover', index: 1 },
            { type: 'index', name: 'index', index: 2 },
        ];

        let index = 3;
        const isMice = isMiceCatalog(tourCatalog);
        tourCatalog.tourPackages
            .sort((a, b) => {
                const tourPackagesCategoryOrder = (tourCatalog as any).tourPackagesCategoryOrder || [];
                const categoryAIndex = tourPackagesCategoryOrder.indexOf(a.category);
                const categoryBIndex = tourPackagesCategoryOrder.indexOf(b.category);
                if (categoryAIndex !== categoryBIndex) return categoryAIndex - categoryBIndex;
                if (a.subcategory && b.subcategory && a.subcategory !== b.subcategory) {
                    return isMice
                        ? compareMiceSubcategories(a.subcategory, b.subcategory)
                        : b.subcategory.localeCompare(a.subcategory);
                }
                return a.name.localeCompare(b.name);
            })
            .forEach((tourPackage) => {
                let name = (tourPackage.category + "-" + tourPackage.name + (tourPackage.subtitle ? "-" + tourPackage.subtitle : "")).toLowerCase();
                name = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                descriptors.push({ type: 'package', name, index, tourPackage });
                index++;
            });

        tourCatalog.otherContents?.forEach((content) => {
            const name = content.title.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            descriptors.push({ type: 'other', name, index, otherContent: content });
            index++;
        });

        if (tourCatalog.hotels?.length > 0) {
            descriptors.push({ type: 'hotels', name: 'Hoteles', index, hotels: tourCatalog.hotels });
        }

        setSlidesContent(descriptors);
    }, [tourOperator]);

    // Una vez que las slides se han renderizado, recorrer cada contenedor y capturarlo

    const handlePrevious = useCallback(() => {
        swiperRef?.slidePrev();
    }, [swiperRef]);

    const handleNext = useCallback(() => {
        swiperRef?.slideNext();
    }, [swiperRef]);

    const handleFullScreen = () => {
        if (swiperRef?.el) {
            try {
                if (swiperRef.el.requestFullscreen) {
                    swiperRef.el.requestFullscreen();
                } else if (swiperRef.el.webkitRequestFullscreen) {
                    // @ts-ignore - Safari support
                    swiperRef.el.webkitRequestFullscreen();
                }
            } catch (err) {
                console.error("Fullscreen request failed:", err);
            }
        }
    };

    // Thumb URLs derived from descriptors — no JSX, no state
    const thumbUrls = useMemo<string[]>(() => {
        if (!tourOperator || !catName || slidesContent.length === 0) return [];
        // MICE no tiene miniaturas generadas en el CDN: se usa la foto de cada página, reducida
        if (isMiceCatalog(tourCatalog ?? undefined)) {
            return slidesContent.map(slide => cdnImageWidth(
                slide.type === 'package'
                    ? slide.tourPackage.introductionImage?.file || slide.tourPackage.mainImage?.file
                    : tourCatalog?.indexImage?.file,
                320
            ) ?? '');
        }
        // Last slide has no thumbnail (e.g. hotels page), so we drop it
        const resolvedCatName = catName;
        return slidesContent.slice(0, -1).map((_, i) =>
            `${cdnUrl}${tourOperator.name}_${resolvedCatName}_thumb_${i + 1}.png`.toLowerCase()
        );
    }, [slidesContent, tourOperator, catName, tourCatalog]);

    // CDN-based thumb slides — no html2canvas, computed from URLs
    const cdnThumbSlides = useMemo<React.ReactElement[]>(() =>
        thumbUrls.map((url, index) => (
            <SwiperSlide key={index} virtualIndex={index + 1}>
                <img
                    src={url}
                    alt={`Thumb ${index}`}
                    className="w-full h-full object-contain cursor-pointer"
                />
            </SwiperSlide>
        )),
    [thumbUrls]);

    // Merge: canvas thumbnails (createThumb path) take priority over CDN thumbnails
    const effectiveThumbSlides = canvasThumbSlides.length > 0 ? canvasThumbSlides : cdnThumbSlides;

    // Main slides computed from data descriptors — JSX is never stored in state
    const slides = useMemo<React.ReactElement[]>(() => {
        if (!tourOperator || !tourCatalog || slidesContent.length === 0) return [];
        const isMice = isMiceCatalog(tourCatalog);
        return slidesContent.map((slide, i) => {
            let content: React.ReactNode;
            switch (slide.type) {
                case 'cover':
                    content = (
                        <TourCatalogCover
                            tourCatalog={tourCatalog}
                            tourOperatorLogoUrl={tourOperator.logoContrast.file ?? ''}
                        />
                    );
                    break;
                case 'index':
                    content = isMice ? (
                        <MiceCatalogIndex
                            goToSlide={goToSlide}
                            tourCatalog={tourCatalog}
                            tourOperator={tourOperator}
                            selection={miceSelection}
                            onSelectionChange={setMiceSelection}
                        />
                    ) : (
                        <TourCatalogIndex
                            goToSlide={goToSlide}
                            tourCatalog={tourCatalog}
                            tourOperator={tourOperator}
                        />
                    );
                    break;
                case 'package':
                    content = isMice ? (
                        <MiceCatalogContent
                            tourOperator={tourOperator}
                            tourPackage={slide.tourPackage as MicePackage}
                            goToSlide={goToSlide}
                            onBackToIndex={sel => setMiceSelection(prev => ({...sel, query: prev.query}))}
                        />
                    ) : (
                        <TourCatalogContent
                            tourOperator={tourOperator}
                            tourPackage={slide.tourPackage}
                            tourCatalog={tourCatalog}
                            goToSlide={goToSlide}
                        />
                    );
                    break;
                case 'other':
                    content = (
                        <TourCatalogAdditionalContent
                            tourOperator={tourOperator}
                            otherContent={slide.otherContent}
                            tourCatalog={tourCatalog}
                            goToSlide={goToSlide}
                        />
                    );
                    break;
                case 'hotels':
                    content = (
                        <TourCatalogAdditionalContentHotels
                            tourOperator={tourOperator}
                            hotels={slide.hotels}
                            tourCatalog={tourCatalog}
                            goToSlide={goToSlide}
                        />
                    );
                    break;
            }
            return (
                <SwiperSlide
                    className="bg-white text-black"
                    key={i}
                    virtualIndex={i}
                    data-hash={slide.name}
                    data-history={slide.name}
                >
                    <div
                        ref={slidesRef[i]}
                        id={`slide-${i}`}
                        className="h-full w-full flex items-center justify-center bg-[#FFFFFF]"
                    >
                        {content}
                    </div>
                </SwiperSlide>
            );
        });
    }, [tourOperator, tourCatalog, slidesContent, goToSlide, slidesRef, miceSelection]);

    // Populate refs only for the createThumb path (html2canvas needs DOM nodes for all slides)
    useEffect(() => {
        if (!isCreatingThumbs || slidesContent.length === 0) return;
        setSlidesRef(slidesContent.map(() => React.createRef<HTMLDivElement>()));
    }, [slidesContent.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Deep-link navigation: run once when both slides data and Swiper are ready
    useEffect(() => {
        if (!swiperRef || slidesContent.length === 0 || hasNavigatedRef.current) return;
        if (window.location.pathname.includes("slides")) {
            const path = window.location.pathname.split("/");
            const slideName = path[path.length - 1].replace(/-/g, " ").replace(" ", "-");
            goToSlide(slideName);
            hasNavigatedRef.current = true;
        }
    }, [swiperRef, slidesContent, goToSlide]);



    // Función para ejecutar tareas en lotes usando requestIdleCallback si está disponible
    type Task = () => Promise<any>;

    function runInBatches(tasks: Task[], batchSize: number): Promise<any[]> {
        const results: any[] = [];
        let index = 0;

        return new Promise((resolve, reject) => {
            function processNextBatch() {
                const currentBatch = tasks.slice(index, index + batchSize);
                index += batchSize;

                Promise.all(currentBatch.map(task => task()))
                    .then(batchResults => {
                        results.push(...batchResults);
                        if (index < tasks.length) {
                            scheduleNext(processNextBatch);
                        } else {
                            resolve(results);
                        }
                    })
                    .catch(reject);
            }

            function scheduleNext(callback: () => void) {
                if ('requestIdleCallback' in window) {
                    requestIdleCallback((deadline) => {
                        if (deadline.timeRemaining() > 20 || deadline.didTimeout) {
                            callback();
                        } else {
                            setTimeout(callback, 50); // fallback si no hay tiempo suficiente
                        }
                    });
                } else {
                    setTimeout(callback, 50);
                }
            }

            scheduleNext(processNextBatch);
        });
    }




    useEffect(() => {

        //get createThumbs from queryParams
        const urlParams = new URLSearchParams(window.location.search);

//urlParams.get("createThumb") === "true" &&
        if(!isMobile && slidesRef && slidesRef.length > 0 && slidesRef.filter(
            (slideRef) => slideRef.current
        ).length === slidesRef.length
        ) {
            if(urlParams.get("createThumb") === "true") {
                const canvasOptions = {
                    backgroundColor: null,
                    useCORS: true,
                    allowTaint: true,
                    scale: 0.5,
                    ignoreElements: (element: HTMLElement) => {
                        const computedStyle = getComputedStyle(element);
                        return computedStyle.color.includes('oklch'); // Ignora si usa color oklch
                    }
                };

                const tasks = slidesRef.slice(1).map(slideRef =>
                    // null check is already guaranteed by the filter guard above
                    () => html2canvas(slideRef.current!, canvasOptions)
                );

                runInBatches(tasks, 4)
                    .then((canvasImages) => {
                        const allThumbSlides = canvasImages.map((canvasImage, index) => (
                            <SwiperSlide key={index} virtualIndex={index + 1}>
                                <img
                                    src={canvasImage.toDataURL()}
                                    alt={`Thumb ${index}`}
                                    className="w-full h-full object-contain cursor-pointer"
                                />
                            </SwiperSlide>
                        ));
                        setCanvasThumbSlides(allThumbSlides);
                    })
                    .catch((error) => {
                        console.error("Error generating slide images", error);
                    });
            }
        }

    }, [slidesRef]);



    const containerStyle = {
        "--heading-font": "var(--heading-font)",
        "--text-font": "var(--text-font)",
        fontFamily: "var(--text-font)",
        height: "100%",
        maxHeight: "100vh",
        overflow: "hidden"
    } as React.CSSProperties;

    const swiperStyle = {
        "--swiper-pagination-color": tourOperator?.primaryColor,
        "--swiper-pagination-progressbar-size": "8px",
    } as React.CSSProperties;

    if(!slides || slides.length === 0) {
        return <div>Cargando...</div>
    }

    return (
        <>
            <Helmet>
                <title>
                     {tourOperator?.name} - Catálogo {catName.charAt(0).toUpperCase() + catName.slice(1)}
                </title>
                {/* Set favicon using tour operator's logo if available */}
                {tourOperator.favicon?.file && (
                    <link
                        rel="icon"
                        type="image/png"
                        href={tourOperator.favicon.file}
                    />
                )}
                {/* Additional meta tags */}
                <meta name="description" content="" />
            </Helmet>
            <div className={classNames(["w-screen h-screen flex flex-col items-center overflow-hidden ", activeIndex === 0 ? "bg-[#1C1C1C]" : "bg-[#F9F9F9]"])}
                 style={containerStyle} ref={containerRef}>
                {/* Header Bar */}
                {mode != "full-screen" ?
                    <div
                        ref={headerRef}
                        className="w-full bg-[#F9F9F9] flex px-4 justify-between items-center flex-shrink-0"
                        style={{height: '50px'}}
                    >
                        <div className="w-2/3 md:w-[200px]">
                            <img
                                src={tourCatalog?.overrideTourOperatorLogo?.file || tourOperator?.logo.file}
                                alt="Logo"
                                className="max-h-[35px] object-contain"
                            />
                        </div>
                        <div className="w-1/3 md:w-[100px] text-black text-sm flex justify-end">
                            {activeIndex + 1} / {slides.length}
                        </div>
                        <div className="flex md:w-[200px] justify-center items-center gap-2">
                            {!isMobile && effectiveThumbSlides.length > 0 && (
                                <div
                                    className="text-black cursor-pointer"
                                    onClick={() => setShowThumb(!showThumb)}
                                >
                                    <PagesThumbIcon/>
                                </div>
                            )}
                            {!isMobile && (
                                <div
                                    className="text-black cursor-pointer"
                                    onClick={handleFullScreen}
                                >
                                    <FullScreenIcon/>
                                </div>)}
                        </div>
                    </div> : null}
                <div className="global-progress-bar">
                    <div className="progress-fill" style={{
                        backgroundColor: tourOperator?.primaryColor
                    }} ref={progressRef}></div>
                </div>
                {/* Slider Container */}
                <div
                    className={classNames([activeIndex === 0 ? '' : 'max-w-[1440px]',"w-full  flex items-center justify-center arrow-anim-cursor-move flex-grow relative h-full"])}
                    style={{
                        height: sliderHeight,
                        maxHeight: isMobile ? sliderHeight : mode !== "full-screen" ? 'calc(100vh - 0px)' : '100vh',
                    }}
                >
                    {/* Main Content - Always Full Width */}
                    <div className="w-full h-full flex relative">
                        {/* Left Arrow */}
                        <div onClick={handlePrevious}
                             className={`absolute top-1/2 left-2 md:left-4 z-10 transform -translate-y-1/2 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
                        >
                            <div
                                className="size-8 flex justify-center items-center cursor-pointer rounded-sm"
                                style={{backgroundColor: '#F9F9F9'}}
                            >
                                <ArrowLeftIcon className="size-6"/>
                            </div>
                        </div>

                        {/* Main Swiper - Always Full Width */}
                        <Swiper
                            className="w-full h-full overflow-hidden"
                            modules={[EffectFade, Pagination, Thumbs, Virtual, Keyboard, Mousewheel, HashNavigation, History]}
                            virtual={{
                                enabled: true,
                                // createThumb path needs all slides in DOM for html2canvas;
                                // normal path uses true virtualization (2 neighbours each side)
                                addSlidesBefore: isCreatingThumbs ? slidesContent.length : 2,
                                addSlidesAfter: isCreatingThumbs ? slidesContent.length : 2,
                                cache: true,
                            }}
                            effect={isMobile ? 'slide' : 'fade'}
                            keyboard={{enabled: true, onlyInViewport: true}}
                            style={swiperStyle}
                            spaceBetween={0}
                            hashNavigation={true}
                            history={{
                                enabled: true,
                                root: window.location.host.includes(tourOperator?.name.toLowerCase()) ? `/${catName ? catName + "/" : ""}` : `/${tourOperator?.name.toLowerCase()}/${catName ? catName + "/" : ""}`,
                            }
                            }
                            mousewheel={false}
                            sensitivity={isMobile ? 1 : 0.2}

                            slidesPerView={1}
                            thumbs={{swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null}} // Evita errores si thumbsSwiper no está inicializado
                            onSlideChange={(sw) => {
                                setActiveIndex(sw.activeIndex)
                                const progress = (sw.activeIndex / (sw.slides.length - 1)) * 100;
                                if (progressRef.current) {
                                    progressRef.current.style.width = `${progress}%`;
                                }}}
                            onSwiper={setSwiperRef}
                        >
                            {slides}
                        </Swiper>

                        {/* Right Arrow */}
                        <div onClick={handleNext}
                             className={`absolute top-1/2 right-2 md:right-4 z-10 transform -translate-y-1/2 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
                        >
                            <div

                                className="size-8 flex justify-center items-center cursor-pointer rounded-sm"
                                style={{backgroundColor: '#F9F9F9'}}
                            >
                                <ArrowRightIcon className="size-6"/>
                            </div>
                        </div>

                    </div>


                </div>
                {/* Thumbnails - Positioned Absolutely */}
                {!isMobile && (
                    <div
                        ref={thumbColumnRef}
                        className={` absolute right-0 top-[50px] h-full bg-[#F1F1F1] transition-all duration-300 ease-in-out pb-20`}
                        style={{
                            zIndex: 5,
                            width: showThumb ? `${thumbWidth}px` : '0',
                            minWidth: showThumb ? `${thumbWidth}px` : '0',
                        }}
                    >
                        <Swiper
                            className={`w-full h-full ${!showThumb && 'hidden'}`}
                            modules={[Thumbs, Virtual, Mousewheel]}
                            virtual={true}
                            spaceBetween={20}
                            watchSlidesProgress
                            slidesPerView={7}
                            mousewheel={true}
                            direction="vertical"
                            onSwiper={setThumbsSwiper as any}
                        >
                            {effectiveThumbSlides}
                        </Swiper>
                    </div>
                )}
            </div>
        </>
    );
};
